import PostHog from './posthog';
import SessionRecord from '../../records/session';

export const POST_APPROVAL_PLAID_EVENT_VERSION = 1;

export const POST_APPROVAL_PLAID_EVENTS = Object.freeze({
  OFFER_VIEWED: 'post_approval_plaid_offer_viewed',
  LINK_OPENED: 'post_approval_plaid_link_opened',
  LINK_SUCCEEDED: 'post_approval_plaid_link_succeeded',
  LINK_EXITED: 'post_approval_plaid_link_exited',
  SUBMIT_INITIATED: 'post_approval_plaid_submit_initiated',
  SKIPPED: 'post_approval_plaid_skipped',
  PROCESSING_VIEWED: 'post_approval_plaid_processing_viewed',
  PROCESSING_OUTCOME_VIEWED: 'post_approval_plaid_processing_outcome_viewed',
  RESULT_VIEWED: 'post_approval_plaid_result_viewed',
});

export const TERMINAL_OUTCOMES = [
  'uplifted',
  'no_change',
  'identity_mismatch',
  'failed_soft',
  'timed_out',
  'skipped',
  'unexpected_result',
];

export const POST_APPROVAL_PLAID_LINK_EXIT_CODES = Object.freeze([
  'INVALID_MFA',
  'INVALID_CREDENTIALS',
  'link_exited',
]);

const eventNames = new Set(Object.values(POST_APPROVAL_PLAID_EVENTS));
const oneOf = (values) => (value) => values.includes(value);
const opaqueIdentifier = (value) =>
  typeof value === 'string' &&
  (/^(?=.*[a-f])[a-f0-9]{24}$/i.test(value) ||
    /^(?=.*[a-f])[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(
      value
    ));
const attemptIdentifier = (value) =>
  value === 'pre_attempt' || opaqueIdentifier(value);
const boolean = (value) => typeof value === 'boolean';
const nonnegativeInteger = (value) => Number.isSafeInteger(value) && value >= 0;
const optional = (validator) => (value) => value == null || validator(value);
const status = oneOf([
  'offered',
  'link_ready',
  'submitted',
  'processing',
  ...TERMINAL_OUTCOMES,
]);
const correlationValidators = {
  applicationId: opaqueIdentifier,
  attemptId: attemptIdentifier,
  attemptStatus: status,
  sessionType: oneOf([
    'checkout',
    'apply_now',
    'standalone',
    'pi4_session',
    'pay_now',
    'consolidation',
    'instore',
    'recharge',
  ]),
  hasShoppingCart: boolean,
};
const sharedProperties = {
  accountReady: boolean,
  underwritingEnhanced: boolean,
};
const creditLimitProperties = {
  baselineCreditLimitCents: optional(nonnegativeInteger),
  effectiveCreditLimitCents: optional(nonnegativeInteger),
  creditLimitDeltaCents: optional(nonnegativeInteger),
};
const eventProperties = {
  [POST_APPROVAL_PLAID_EVENTS.OFFER_VIEWED]: {
    source: oneOf(['offer']),
    hasAttempt: boolean,
    baselineCreditLimitCents: optional(nonnegativeInteger),
    ...sharedProperties,
  },
  [POST_APPROVAL_PLAID_EVENTS.LINK_OPENED]: {},
  [POST_APPROVAL_PLAID_EVENTS.LINK_SUCCEEDED]: {
    accountSelection: oneOf(['single', 'choose', 'none']),
    accountCount: nonnegativeInteger,
  },
  [POST_APPROVAL_PLAID_EVENTS.LINK_EXITED]: {
    exitCode: oneOf(POST_APPROVAL_PLAID_LINK_EXIT_CODES),
    errorCode: oneOf(['user_exit', 'link_exited']),
  },
  [POST_APPROVAL_PLAID_EVENTS.SUBMIT_INITIATED]: {
    isRetry: boolean,
    accountSelection: oneOf(['single', 'choose', 'none']),
  },
  [POST_APPROVAL_PLAID_EVENTS.SKIPPED]: {
    result: oneOf(['skipped', 'recovered']),
  },
  [POST_APPROVAL_PLAID_EVENTS.PROCESSING_VIEWED]: {
    deadlineSeconds: optional(nonnegativeInteger),
    ...sharedProperties,
  },
  [POST_APPROVAL_PLAID_EVENTS.PROCESSING_OUTCOME_VIEWED]: {
    timedOut: boolean,
    routedVia: oneOf(['path', 'route']),
    waitMs: nonnegativeInteger,
    submitToTerminalMs: optional(nonnegativeInteger),
    pollCount: nonnegativeInteger,
    exhaustedRetries: boolean,
    ...sharedProperties,
  },
  [POST_APPROVAL_PLAID_EVENTS.RESULT_VIEWED]: {
    ...sharedProperties,
    ...creditLimitProperties,
    errorCode: optional(oneOf(['processing_failed', 'unexpected_result'])),
    tierChanged: boolean,
    virtualCardEligible: boolean,
    virtualCardActive: boolean,
    virtualCardNewlyEligible: boolean,
    vcSubLimitCents: optional(nonnegativeInteger),
  },
};

const read = (value, key) => value?.get?.(key) ?? value?.[key];

const toPlainObject = (value) => value?.toJS?.() ?? value ?? {};

const hasShoppingCart = (session) => {
  if (typeof session?.hasShoppingCart === 'function') {
    return session.hasShoppingCart();
  }

  return Boolean(
    read(session, 'hasShoppingCart') ?? read(session, 'shopping_cart_id')
  );
};

const safeProperties = (name, properties) =>
  Object.fromEntries(
    Object.entries(properties).filter(
      ([key, value]) => eventProperties[name][key]?.(value) === true
    )
  );

export const capturePostApprovalPlaidEvent = (name, eventProperties = {}) => {
  if (!eventNames.has(name)) {
    if (process.env.NODE_ENV === 'test') {
      throw new Error(`Unknown post-approval Plaid event: ${name}`);
    }

    return false;
  }

  const { session, attempt, applicationId, ...props } =
    toPlainObject(eventProperties);
  const resolvedApplicationId =
    applicationId === undefined
      ? read(session, 'application_id')
      : applicationId;
  const trustedNumericApplicationId =
    session instanceof SessionRecord &&
    read(session, 'application_id') === resolvedApplicationId &&
    nonnegativeInteger(resolvedApplicationId) &&
    resolvedApplicationId <= 999999999;

  try {
    const correlation = {
      applicationId: resolvedApplicationId,
      attemptId: read(attempt, 'attempt_id'),
      attemptStatus: read(attempt, 'status'),
      sessionType: read(session, 'session_type'),
      hasShoppingCart: hasShoppingCart(session),
    };
    const validCorrelation = Object.fromEntries(
      Object.entries(correlation).filter(
        ([key, value]) =>
          correlationValidators[key](value) ||
          (key === 'applicationId' && trustedNumericApplicationId)
      )
    );
    return new PostHog().capture(name, {
      ...safeProperties(name, props),
      eventVersion: POST_APPROVAL_PLAID_EVENT_VERSION,
      ...validCorrelation,
    });
  } catch (_error) {
    return false;
  }
};
